import { getDefaultStore } from "jotai";
import { isAuthEnabled, isPersistenceEnabled } from "../../utils/environment";
import { colors } from "../../utils/style";
import { hasDirtyAtom, isOnlineAtom, syncStateAtom } from "../atoms";
import logger from "../../utils/logger";

const ERROR_TIME_THRESHOLD_IN_SECONDS = 60 * 2; // 2 minutes;

const SyncStatuses = {
  ok: { id: "ok", message: "Everything is synced", color: colors.bg.ok },
  booting: { id: "booting", message: "Booting...", color: colors.bg.tertiary },
  offline: {
    id: "offline",
    message: "You are offline",
    color: colors.bg.offline,
  },
  disabled: {
    id: "disabled",
    message: "Persistence is disabled",
    color: colors.bg.warning,
  },
  warning: { id: "warning", message: "Syncing...", color: colors.bg.warning },
  error: {
    id: "error",
    message: "There's an issue with syncing",
    color: colors.bg.error,
  },
} as const;
type SyncStatus = (typeof SyncStatuses)[keyof typeof SyncStatuses];

export function computeSyncStatus(): SyncStatus {
  const syncState = getDefaultStore().get(syncStateAtom);
  const isOnline = getDefaultStore().get(isOnlineAtom);
  const now = Date.now();
  const hasDirty = getDefaultStore().get(hasDirtyAtom);

  if (!isPersistenceEnabled || !isAuthEnabled) {
    return SyncStatuses.disabled;
  }

  if (now - syncState.startTime < 10_000) {
    return SyncStatuses.booting;
  }

  if (!isOnline) {
    return SyncStatuses.offline;
  }

  if (
    syncState.pendingUpdateCount >= 4 &&
    Date.now() - syncState.lastFullySyncedAt > ERROR_TIME_THRESHOLD_IN_SECONDS * 1000 &&
    (syncState.appNoteCount >= syncState.serverNoteCount || syncState.appFolderCount >= syncState.serverFolderCount)
  ) {
    logger.error("In Error State", {
      context: {
        lastFullySyncedAt: syncState.lastFullySyncedAt,
        pendingUpdateCount: syncState.pendingUpdateCount,
        hasDirty: hasDirty,
        appNoteCount: syncState.appNoteCount,
        serverNoteCount: syncState.serverNoteCount,
        appFolderCount: syncState.appFolderCount,
        serverFolderCount: syncState.serverFolderCount,
      },
    });
    return SyncStatuses.error;
  }

  if (syncState.pendingUpdateCount > 0 || syncState.appNoteCount !== syncState.serverNoteCount || hasDirty) {
    return SyncStatuses.warning;
  }

  return SyncStatuses.ok;
}
